import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsApi, EventFormData, calendarApi, CalendarEvent } from '../services/api';
import {
    addDays,
    startOfWeek,
    format,
    setHours,
    setMinutes,
    isSameDay,
    parseISO
} from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { ArrowLeft, ArrowRight, Clock, Send, Save, Globe } from 'lucide-react';

const DURATIONS = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
];

const HOURS = Array.from({ length: 12 }, (_, i) => i + 7); // 7 AM to 6 PM

const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'UTC', label: 'UTC' },
];

export function CreateEventPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEditing = !!id;

    // Form state
    const [eventName, setEventName] = useState('');
    const [comments, setComments] = useState('');
    const [duration, setDuration] = useState(60);
    const [recipients, setRecipients] = useState('');
    const [timezone, setTimezone] = useState('America/New_York');

    // Calendar state
    const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));
    const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());

    // Outlook calendar availability
    const [busyTimes, setBusyTimes] = useState<CalendarEvent[]>([]);
    const [calendarConnected, setCalendarConnected] = useState(false);
    const [loadingCalendar, setLoadingCalendar] = useState(false);

    // UI state
    const [step, setStep] = useState<1 | 2>(1);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Auto-detect user's timezone
    useEffect(() => {
        try {
            const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
            // Only set if it's a common US timezone
            const usTimezones = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix'];
            if (usTimezones.includes(detected)) {
                setTimezone(detected);
            }
        } catch {
            // Keep default America/New_York
        }
    }, []);

    // Load existing event if editing
    useEffect(() => {
        if (id) {
            loadEvent(id);
        }
    }, [id]);

    // Load calendar availability when week changes (for step 2)
    useEffect(() => {
        if (step === 2) {
            loadCalendarAvailability();
        }
    }, [weekStart, step]);

    const loadCalendarAvailability = async () => {
        setLoadingCalendar(true);
        try {
            const start = weekStart.toISOString();
            const end = addDays(weekStart, 7).toISOString();
            const result = await calendarApi.getAvailability(start, end);
            setCalendarConnected(result.connected);
            setBusyTimes(result.events);
        } catch (err) {
            console.error('Failed to load calendar:', err);
        } finally {
            setLoadingCalendar(false);
        }
    };

    // Check if a time slot overlaps with any busy time
    const isBusy = (slotTime: Date): { busy: boolean; event?: CalendarEvent } => {
        if (!calendarConnected || busyTimes.length === 0) {
            return { busy: false };
        }

        // Convert slot time to UTC for comparison
        // Format as string first to avoid browser timezone issues
        const dateStr = format(slotTime, 'yyyy-MM-dd HH:mm');
        const slotUtc = zonedTimeToUtc(dateStr, timezone);
        const slotStart = slotUtc.getTime();
        // Each grid row is 30 minutes, use this for overlap detection
        // This ensures a 2-hour meeting blocks all 4 half-hour rows
        const slotEnd = slotStart + 30 * 60 * 1000;

        for (const event of busyTimes) {
            // Calendar events are already in UTC (we convert them on the backend)
            const eventStart = parseISO(event.start).getTime();
            const eventEnd = parseISO(event.end).getTime();

            // Check for overlap
            if (slotStart < eventEnd && slotEnd > eventStart) {
                return { busy: true, event };
            }
        }

        return { busy: false };
    };

    const loadEvent = async (eventId: string) => {
        setIsLoading(true);
        try {
            const event = await eventsApi.get(eventId);
            setEventName(event.event_name);
            setComments(event.comments || '');
            setDuration(event.duration_minutes);

            if (event.recipients) {
                setRecipients(event.recipients.map(r => r.email).join('\n'));
            }

            if (event.time_slots) {
                const slots = new Set(event.time_slots.map(s => s.start_time_utc));
                setSelectedSlots(slots);
            }
        } catch (err: any) {
            alert(err.message || 'Failed to load event');
            navigate('/dashboard');
        } finally {
            setIsLoading(false);
        }
    };

    // Parse recipients from text
    const parseRecipients = (text: string): string[] => {
        return text
            .split(/[,;\n]+/)
            .map(e => e.trim().toLowerCase())
            .filter(e => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
            .filter((e, i, arr) => arr.indexOf(e) === i); // Remove duplicates
    };

    // Validate step 1
    const validateStep1 = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!eventName.trim()) {
            newErrors.eventName = 'Event name is required';
        }

        const emails = parseRecipients(recipients);
        if (emails.length === 0) {
            newErrors.recipients = 'At least one valid email is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle slot selection
    const toggleSlot = (dateTime: Date) => {
        // Format the date/time as a string in YYYY-MM-DD HH:mm format
        // This represents the "wall clock" time the user sees
        const dateStr = format(dateTime, 'yyyy-MM-dd HH:mm');

        // Parse this string in the target timezone, then convert to UTC
        const utcTime = zonedTimeToUtc(dateStr, timezone).toISOString();

        setSelectedSlots(prev => {
            const next = new Set(prev);
            if (next.has(utcTime)) {
                next.delete(utcTime);
            } else {
                next.add(utcTime);
            }
            return next;
        });
    };

    // Check if a slot is selected
    const isSlotSelected = (dateTime: Date): boolean => {
        const dateStr = format(dateTime, 'yyyy-MM-dd HH:mm');
        const utcTime = zonedTimeToUtc(dateStr, timezone).toISOString();
        return selectedSlots.has(utcTime);
    };

    // Check if slot falls within the duration of ANY selected event
    const isSlotOccupiedBySelection = (slotTime: Date): boolean => {
        const dateStr = format(slotTime, 'yyyy-MM-dd HH:mm');
        const slotUtc = zonedTimeToUtc(dateStr, timezone);
        const slotStart = slotUtc.getTime();
        const slotEnd = slotStart + 30 * 60 * 1000; // 30 min grid slot

        for (const startStr of selectedSlots) {
            const eventStart = parseISO(startStr).getTime();
            const eventEnd = eventStart + duration * 60 * 1000; // Duration of selected event

            // Check overlap
            if (slotStart < eventEnd && slotEnd > eventStart) {
                return true;
            }
        }
        return false;
    };

    // Generate week days
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    // Handle form submission
    const handleSubmit = async (send: boolean = false) => {
        if (selectedSlots.size === 0) {
            alert('Please select at least one time slot');
            return;
        }

        setIsSaving(true);
        try {
            const emails = parseRecipients(recipients);

            const data: EventFormData = {
                event_name: eventName,
                comments: comments || undefined,
                duration_minutes: duration,
                timezone,
                time_slots: Array.from(selectedSlots).map(start => ({
                    start_time_utc: start,
                    end_time_utc: new Date(new Date(start).getTime() + duration * 60000).toISOString()
                })),
                recipients: emails,
            };

            let eventId = id;

            if (isEditing) {
                await eventsApi.update(id!, data);
            } else {
                const created = await eventsApi.create(data);
                eventId = created.id;
            }

            if (send && eventId) {
                await eventsApi.send(eventId);
            }

            navigate('/dashboard');
        } catch (err: any) {
            alert(err.message || 'Failed to save event');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="page container">
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="page container">
            <h1 style={{ marginBottom: 'var(--space-6)' }}>
                {isEditing ? 'Edit Event' : 'Create New Event'}
            </h1>

            {/* Step Indicator */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-4)',
                marginBottom: 'var(--space-6)',
                paddingBottom: 'var(--space-4)',
                borderBottom: '1px solid var(--color-gray-200)'
            }}>
                <button
                    onClick={() => setStep(1)}
                    className={`btn ${step === 1 ? 'btn-primary' : 'btn-secondary'}`}
                >
                    1. Event Details
                </button>
                <button
                    onClick={() => validateStep1() && setStep(2)}
                    className={`btn ${step === 2 ? 'btn-primary' : 'btn-secondary'}`}
                >
                    2. Select Times
                </button>
            </div>

            {/* Step 1: Event Details */}
            {step === 1 && (
                <div className="card" style={{ maxWidth: '600px' }}>
                    <div className="form-group">
                        <label className="form-label">Event Name *</label>
                        <input
                            type="text"
                            className={`form-input ${errors.eventName ? 'error' : ''}`}
                            value={eventName}
                            onChange={(e) => setEventName(e.target.value)}
                            placeholder="e.g., Project Kickoff Meeting"
                        />
                        {errors.eventName && <p className="form-error">{errors.eventName}</p>}
                    </div>

                    <div className="form-group">
                        <label className="form-label">Comments (optional)</label>
                        <textarea
                            className="form-textarea"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Add any additional context for recipients..."
                            rows={3}
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Recipients *</label>
                        <textarea
                            className={`form-textarea ${errors.recipients ? 'error' : ''}`}
                            value={recipients}
                            onChange={(e) => setRecipients(e.target.value)}
                            placeholder="Enter email addresses separated by comma, semicolon, or new line..."
                            rows={4}
                        />
                        {errors.recipients && <p className="form-error">{errors.recipients}</p>}
                        <p className="form-hint">
                            {parseRecipients(recipients).length > 0
                                ? `${parseRecipients(recipients).length} valid email(s) detected`
                                : 'Paste multiple emails at once'}
                        </p>
                    </div>

                    {/* Timezone Selector */}
                    <div className="form-group">
                        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Globe size={14} />
                            Your Timezone *
                        </label>
                        <select
                            className="form-select"
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                        >
                            {TIMEZONES.map(tz => (
                                <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                        </select>
                        <p className="form-hint">
                            Times you select will be in this timezone
                        </p>
                    </div>

                    {/* Duration Selector */}
                    <div className="form-group">
                        <label className="form-label">
                            <Clock size={14} style={{ display: 'inline', marginRight: '6px' }} />
                            Meeting Duration *
                        </label>
                        <select
                            className="form-select"
                            value={duration}
                            onChange={(e) => setDuration(parseInt(e.target.value))}
                        >
                            {DURATIONS.map(d => (
                                <option key={d.value} value={d.value}>{d.label}</option>
                            ))}
                        </select>
                    </div>


                    <button
                        onClick={() => validateStep1() && setStep(2)}
                        className="btn btn-primary"
                    >
                        Continue to Time Selection
                        <ArrowRight size={18} />
                    </button>
                </div>
            )}

            {/* Step 2: Time Selection */}
            {step === 2 && (
                <div>
                    {/* Duration & Week Navigation */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 'var(--space-4)',
                        flexWrap: 'wrap',
                        gap: 'var(--space-4)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                            <Clock size={18} />
                            <select
                                className="form-select"
                                value={duration}
                                onChange={(e) => setDuration(Number(e.target.value))}
                                style={{ width: 'auto' }}
                            >
                                {DURATIONS.map(d => (
                                    <option key={d.value} value={d.value}>{d.label}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setWeekStart(addDays(weekStart, -7))}
                            >
                                <ArrowLeft size={18} />
                            </button>
                            <span style={{ minWidth: '200px', textAlign: 'center', fontWeight: 500 }}>
                                {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
                            </span>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setWeekStart(addDays(weekStart, 7))}
                            >
                                <ArrowRight size={18} />
                            </button>
                        </div>

                        <div style={{
                            background: 'var(--color-gray-100)',
                            padding: 'var(--space-2) var(--space-3)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            {selectedSlots.size} slot{selectedSlots.size !== 1 ? 's' : ''} selected
                        </div>
                    </div>

                    {/* Outlook Calendar Legend */}
                    {calendarConnected && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-4)',
                            marginBottom: 'var(--space-3)',
                            padding: 'var(--space-3)',
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid #0ea5e9',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <span style={{ color: '#0078d4', fontWeight: 600 }}>📅 Outlook Connected</span>
                                {loadingCalendar && <span style={{ color: 'var(--color-gray-500)' }}>(loading...)</span>}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: 'repeating-linear-gradient(45deg, #fee2e2, #fee2e2 2px, #fecaca 2px, #fecaca 4px)',
                                    borderRadius: '2px',
                                    border: '1px solid #fca5a5'
                                }} />
                                <span>= Busy in Outlook</span>
                            </div>
                        </div>
                    )}

                    {/* Calendar Grid */}
                    <div className="card" style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '80px', padding: 'var(--space-3)' }}></th>
                                    {weekDays.map(day => (
                                        <th
                                            key={day.toISOString()}
                                            style={{
                                                padding: 'var(--space-3)',
                                                textAlign: 'center',
                                                background: isSameDay(day, new Date()) ? 'var(--color-primary-light)' : 'var(--color-gray-50)'
                                            }}
                                        >
                                            <div style={{ fontWeight: 600 }}>{format(day, 'EEE')}</div>
                                            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                                {format(day, 'M/d')}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {HOURS.map(hour => (
                                    <>
                                        {/* Full hour row */}
                                        <tr key={`${hour}:00`}>
                                            <td style={{
                                                padding: 'var(--space-2)',
                                                fontSize: 'var(--font-size-sm)',
                                                color: 'var(--color-gray-500)',
                                                textAlign: 'right',
                                                verticalAlign: 'top'
                                            }}>
                                                {format(setMinutes(setHours(new Date(), hour), 0), 'h:mm a')}
                                            </td>
                                            {weekDays.map(day => {
                                                const slotTime = setMinutes(setHours(day, hour), 0);
                                                const isSelected = isSlotSelected(slotTime);
                                                const isOccupied = isSlotOccupiedBySelection(slotTime);
                                                const busyCheck = isBusy(slotTime);

                                                return (
                                                    <td
                                                        key={slotTime.toISOString()}
                                                        onClick={() => toggleSlot(slotTime)}
                                                        style={{
                                                            padding: '2px',
                                                            cursor: 'pointer'
                                                        }}
                                                        title={busyCheck.busy ? `Busy: ${busyCheck.event?.subject || 'Meeting'}` : undefined}
                                                    >
                                                        <div style={{
                                                            height: '32px',
                                                            background: isOccupied
                                                                ? '#0077b6'
                                                                : busyCheck.busy
                                                                    ? 'repeating-linear-gradient(45deg, #fca5a5, #fca5a5 4px, #f87171 4px, #f87171 8px)'
                                                                    : '#e5e7eb',
                                                            borderRadius: 'var(--radius-sm)',
                                                            transition: 'all var(--transition-fast)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: isSelected ? 'white' : busyCheck.busy ? '#b91c1c' : 'transparent',
                                                            fontSize: 'var(--font-size-xs)',
                                                            border: busyCheck.busy && !isSelected ? '1px solid #ef4444' : 'none'
                                                        }}>
                                                            {isSelected ? '✓' : busyCheck.busy ? '•' : ''}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                        {/* Half hour row */}
                                        <tr key={`${hour}:30`}>
                                            <td style={{
                                                padding: 'var(--space-2)',
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-gray-400)',
                                                textAlign: 'right',
                                                verticalAlign: 'top'
                                            }}>
                                                :30
                                            </td>
                                            {weekDays.map(day => {
                                                const slotTime = setMinutes(setHours(day, hour), 30);
                                                const isSelected = isSlotSelected(slotTime);
                                                const isOccupied = isSlotOccupiedBySelection(slotTime);
                                                const busyCheck = isBusy(slotTime);

                                                return (
                                                    <td
                                                        key={slotTime.toISOString()}
                                                        onClick={() => toggleSlot(slotTime)}
                                                        style={{
                                                            padding: '2px',
                                                            cursor: 'pointer'
                                                        }}
                                                        title={busyCheck.busy ? `Busy: ${busyCheck.event?.subject || 'Meeting'}` : undefined}
                                                    >
                                                        <div style={{
                                                            height: '32px',
                                                            background: isOccupied
                                                                ? '#0077b6'
                                                                : busyCheck.busy
                                                                    ? 'repeating-linear-gradient(45deg, #fca5a5, #fca5a5 4px, #f87171 4px, #f87171 8px)'
                                                                    : '#f3f4f6',
                                                            borderRadius: 'var(--radius-sm)',
                                                            transition: 'all var(--transition-fast)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: isSelected ? 'white' : busyCheck.busy ? '#b91c1c' : 'transparent',
                                                            fontSize: 'var(--font-size-xs)',
                                                            border: busyCheck.busy && !isSelected ? '1px solid #ef4444' : 'none'
                                                        }}>
                                                            {isSelected ? '✓' : busyCheck.busy ? '•' : ''}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    </>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Actions */}
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-3)',
                        marginTop: 'var(--space-5)',
                        justifyContent: 'space-between'
                    }}>
                        <button
                            onClick={() => setStep(1)}
                            className="btn btn-secondary"
                        >
                            <ArrowLeft size={18} />
                            Back
                        </button>

                        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                            <button
                                onClick={() => handleSubmit(false)}
                                disabled={isSaving}
                                className="btn btn-secondary"
                            >
                                <Save size={18} />
                                Save as Draft
                            </button>
                            <button
                                onClick={() => handleSubmit(true)}
                                disabled={isSaving || selectedSlots.size === 0}
                                className="btn btn-primary"
                            >
                                <Send size={18} />
                                {isSaving ? 'Sending...' : 'Send Invitations'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
